import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Heart, TrendingUp, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';

interface ClairsentientEntry {
  id: string;
  created_at: string;
  impression_type: string;
  description: string;
  accuracy_rating?: number;
  outcome_notes?: string;
  tags: string[];
}

export default function ClairsentientJournal() {
  const { goBack, getBackButtonLabel } = useNavigationHistory();
  const [entries, setEntries] = useState<ClairsentientEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    impression_type: '',
    description: '',
    accuracy_rating: undefined as number | undefined,
    outcome_notes: '',
    tags: [] as string[]
  });

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('clairsentience_entries')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast({ title: 'Error loading entries', variant: 'destructive' });
    } else {
      setEntries(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('clairsentience_entries')
      .insert({
        user_id: user.id,
        ...formData
      });

    if (error) {
      toast({ title: 'Error saving entry', variant: 'destructive' });
    } else {
      toast({ title: 'Clairsentient entry saved' });
      setFormData({
        impression_type: '',
        description: '',
        accuracy_rating: undefined,
        outcome_notes: '',
        tags: []
      });
      setShowForm(false);
      loadEntries();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading your clairsentient journal...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            Clairsentience Journal
          </CardTitle>
          <CardDescription>
            Track your psychic impressions, gut feelings, and intuitive hits. Log when they occur and validate accuracy over time to build trust in your abilities.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowForm(!showForm)} className="mb-4">
            <Plus className="h-4 w-4 mr-2" />
            Log New Impression
          </Button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 border border-border rounded-lg bg-card">
              <div className="space-y-2">
                <Label>Impression Type</Label>
                <Select value={formData.impression_type} onValueChange={(value) => setFormData({ ...formData, impression_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gut-feeling">Gut Feeling</SelectItem>
                    <SelectItem value="emotional-hit">Emotional Hit</SelectItem>
                    <SelectItem value="physical-sensation">Physical Sensation</SelectItem>
                    <SelectItem value="knowing">Just Knowing</SelectItem>
                    <SelectItem value="warning">Warning Sign</SelectItem>
                    <SelectItem value="validation">Validation</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>What did you sense?</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the impression, feeling, or knowing that came to you..."
                  rows={4}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Accuracy Rating (optional)</Label>
                <Select 
                  value={formData.accuracy_rating?.toString() || ''} 
                  onValueChange={(value) => setFormData({ ...formData, accuracy_rating: value ? parseInt(value) : undefined })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Rate accuracy if validated" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <SelectItem key={num} value={num.toString()}>{num}/10</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Outcome Notes (optional)</Label>
                <Textarea
                  value={formData.outcome_notes}
                  onChange={(e) => setFormData({ ...formData, outcome_notes: e.target.value })}
                  placeholder="Did this impression prove accurate? What happened?"
                  rows={2}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Save Entry</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {entries.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Start tracking your clairsentient impressions to build trust in your psychic abilities.
              </p>
            ) : (
              entries.map((entry) => (
                <Card key={entry.id} className="bg-muted/30">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <Badge variant="outline">{entry.impression_type}</Badge>
                        {entry.accuracy_rating && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <TrendingUp className="h-3 w-3" />
                            Accuracy: {entry.accuracy_rating}/10
                          </div>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(entry.created_at), 'PPp')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm font-light">{entry.description}</p>
                    {entry.outcome_notes && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-sm font-light text-primary mb-1">Outcome:</p>
                        <p className="text-sm text-muted-foreground font-light">{entry.outcome_notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
